import { useCallback, useEffect, useRef, useState } from 'react';
import { attachEngineHandlers } from '../engine/index.js';

/* The engine owns the review state.
 *
 * It keeps `uploadedFiles`, `_lastFormData`, `_scrapedSite` and the derived
 * objects on the window, and it writes report HTML straight into the DOM. That
 * is not how a React app would be built from scratch, and it is deliberately
 * not being changed here: 1,053 assertions cover the engine's behaviour and
 * they hold only while the code stays as it is.
 *
 * So this hook does the smallest honest thing — it binds the engine's handlers
 * once the form exists, and gives components a way to read what the engine has
 * put on the window. Untangling that is the next change, not this one.
 */
export function useEngineHandlers(ready) {
  const bound = useRef(0);
  useEffect(() => {
    if (!ready) return;
    bound.current = attachEngineHandlers();
  }, [ready]);
  return bound.current;
}

/** Read a value the engine keeps on the window, re-reading on demand. */
export function useEngineValue(key, initial = null) {
  const [value, setValue] = useState(() => (typeof window !== 'undefined' ? window[key] : initial) ?? initial);
  const refresh = useCallback(() => {
    setValue((typeof window !== 'undefined' ? window[key] : initial) ?? initial);
  }, [key, initial]);
  return [value, refresh];
}

/** Files the engine is holding for this review. */
export function useUploadedFiles() {
  const [files, setFiles] = useState([]);
  const refresh = useCallback(() => {
    setFiles(Array.from((typeof window !== 'undefined' && window.uploadedFiles) || []));
  }, []);
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 400);   // the engine mutates the array in place
    return () => clearInterval(t);
  }, [refresh]);
  return [files, refresh];
}
